import React, { Suspense, useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router";

import MessageViewer from "../notifications/MessageViewer";
import RedirectToHttps from "../shared/redirectToHttps";
import localRepository from "../../localRepository";
import NativeAppMessagingContainer from "../nativeAppMessaging";
import { makeStyles } from "@mui/styles";

// new material
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import mgTheme from "assets/mgTheme";
import { useAuth0, useRefreshToken } from "../Auth0/useAuth";
import { logInUserAnalytics, logOutUserAnalytics } from "libs/amplitudeHelper";
import { correlationId } from "libs/correlationId";
import LoadingView from "components/styled/loadingView/LoadingView";
import { AsyncRecoverPassword } from "./LazyComponents";
import Signin from "componentes/auth/Signin";
import { Callback } from "componentes/Auth0/Callback";
import LoginFormContainer from "componentes/user/LoginContainer";
import useHotjar from "components/app/useHotjar";
import AppRoutes from "components/app/Routes";
import AuthenticationRoutes from "components/app/authentication/Routes";
import { useFeatureFlags } from "../shared/featureFlags";
import environment from "environment";
import useLocationAnalytics from "components/app/useLocationAnalytics";
import Headers from "./Headers";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  withLDProvider,
  useLDClient,
  useFlags,
} from "launchdarkly-react-client-sdk";
import { basicLogger } from "launchdarkly-js-client-sdk";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BottomHeightProvider } from "components/contexts/bottomActions/bottomHeight.provider";
import { FiltroEstadoProvider } from "components/contexts/filtroEstado/filtroEstado.provider";
import { LogsProvider } from "contexts/logger/logger.provider";
import { External } from "componentes/auth/External";
import { sendMessageToNative } from "componentes/nativeAppMessaging/js/actions";
import { isUserTypeInMaintenance } from "components/maintenance/underMaintenanceConfig";
import Maintenance from "components/maintenance/Maintenance";
import { ThemeProvider as StylesThemeProvider } from "@mui/styles";

const PublicViews = () => {
  return (
    <Routes>
      <Route path="/signin" element={<Signin />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/validating/external" element={<External />} />
      <Route path="/loading" element={<LoadingView />} />
      <Route path="/login/old" element={<LoginFormContainer />} />
      <Route path="/login/password/:token" element={<AsyncRecoverPassword />} />
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  );
};

const AuthenticatedViews = () => {
  useLocationAnalytics();
  return <AppRoutes />;
};

const AppContent = ({
  queryClient,
  ldClient,
  isAuthenticated,
  authState,
  isLoggedIn,
  isOnNativeApp,
}) => {
  const location = useLocation();
  return (
    <>
      <NativeAppMessagingContainer />
      <LogsProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={mgTheme}>
            <StylesThemeProvider theme={mgTheme}>
              <CssBaseline />
              <NonScrollableBodyDefault>
                <QueryClientProvider client={queryClient}>
                  <BottomHeightProvider>
                    <FiltroEstadoProvider>
                      <ReactQueryDevtools />
                      <RedirectToHttps>
                        <Suspense fallback={<LoadingView />}>
                          {ldClient && (
                            <>
                              <Headers />
                              <MessageViewer />
                              {isAuthenticated &&
                                authState.user &&
                                authState.user.isAuthorized && (
                                  <AuthenticatedViews
                                    location={location}
                                    isOnNativeApp={isOnNativeApp}
                                  />
                                )}
                              {isAuthenticated &&
                                !(
                                  authState.user && authState.user.isAuthorized
                                ) && <AuthenticationRoutes />}
                              {!isAuthenticated && !isLoggedIn && (
                                <PublicViews />
                              )}
                            </>
                          )}
                          {(!ldClient || (!isAuthenticated && isLoggedIn)) && (
                            <LoadingView />
                          )}
                        </Suspense>
                      </RedirectToHttps>
                    </FiltroEstadoProvider>
                  </BottomHeightProvider>
                </QueryClientProvider>
              </NonScrollableBodyDefault>
            </StylesThemeProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </LogsProvider>
    </>
  );
};

const NonScrollableBodyDefault = ({ children }) => {
  const useAppStyles = makeStyles({
    nonScrollableBodyDefault: {
      backgroundColor: "#f9f7fc",
      width: "100%",
    },
  });

  const classes = useAppStyles();

  useEffect(() => {
    document.body.className = classes.nonScrollableBodyDefault;
  }, [classes.nonScrollableBodyDefault]);

  return <>{children}</>;
};

const App = ({ isOnNativeApp, nativeCredentials }) => {
  const { isAuthenticated, authState } = useAuth0();
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  useRefreshToken(isOnNativeApp, nativeCredentials);

  useEffect(() => {
    if (isLoggedIn && authState.user) {
      logInUserAnalytics(
        authState.user.email,
        authState.user.preferred_username,
        authState.user.sub,
        authState.user.euroUserId,
        localRepository.version.get(),
      );
      if (authState.user.euroUserId)
        correlationId.setUserPart(String(authState.user.euroUserId));
    } else {
      logOutUserAnalytics();
      // Solo al cerrar sesion de verdad. Si el usuario queda momentaneamente en
      // null durante un refresh de token, isLoggedIn sigue en true y no hay que
      // regenerar el id de sesion en el medio.
      if (!isLoggedIn) correlationId.clear();
    }
  }, [authState.user, isLoggedIn]);

  useFeatureFlags();
  const ldClient = useLDClient();
  const flags = useFlags();

  useHotjar();

  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { retry: 2 } } }),
    [],
  );

  // Notify native app when the web app is fully loaded
  useEffect(() => {
    if (isOnNativeApp && ldClient && (isAuthenticated || !isLoggedIn)) {
      sendMessageToNative({
        type: "WEB_APP_LOADED",
      });
    }
  }, [isOnNativeApp, ldClient, isAuthenticated, isLoggedIn]);

  // Check if user type is in maintenance mode (after all hooks)
  if (
    ldClient &&
    isAuthenticated &&
    authState.user &&
    isUserTypeInMaintenance(flags, authState.user.userType)
  ) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mgTheme}>
          <StylesThemeProvider theme={mgTheme}>
            <CssBaseline />
            <Maintenance />
          </StylesThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppContent
        queryClient={queryClient}
        ldClient={ldClient}
        isAuthenticated={isAuthenticated}
        authState={authState}
        isLoggedIn={isLoggedIn}
        isOnNativeApp={isOnNativeApp}
      />
    </BrowserRouter>
  );
};

export default withLDProvider({
  clientSideID: environment.launchDarkly.sdkKey,
  options: {
    logger: basicLogger({
      level: environment.name === "production" ? "error" : "info",
    }),
  },
})(App);
